import classNames from 'classnames'
import { Plugin, PluginKey, Transaction } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'
import { Node } from 'prosemirror-model'

import { PresenceAvatars } from 'common/PresenceAvatars/PresenceAvatars'
import Styles from './ElementPresence.module.scss'
import { UserElementPresences } from 'types'
import { createRoot, Root } from 'react-dom/client'

export interface ElementPresences {
  [userId: string]: UserElementPresences
}

interface ElementPresenceState {
  decorations: DecorationSet<any>
  roots: Root[]
}

interface NodePosition {
  node: Node
  prevNode: Node
  pos: number
}

const ElementPresencePluginKey = new PluginKey('elementPresence')

const elementPresenceClass = (node?: Node) => {
  // the first node in the doc
  if (!node) return 'nothing'
  if (node.type.name === 'heading') return `h${node.attrs.level - 1}`
  return node.type.name
}

const calcNodePositionByUuid = (doc: Node) => {
  const nodePositionByUuid: { [uuid: string]: NodePosition } = {}

  let prevNode: Node
  doc.forEach((node, pos) => {
    nodePositionByUuid[node.attrs.blockId] = { node, pos, prevNode }
    prevNode = node
  })

  return nodePositionByUuid
}

const findNodePosition = (
  nodePositionByUuid: { [uuid: string]: NodePosition },
  { current, previous }: UserElementPresences,
) => {
  if (!current.element_uuid) return

  // show near the current element uuid if it exists
  let nodePosition = nodePositionByUuid[current.element_uuid]
  // keep showing near the previous elemenet uuid if the new uuid is not in the doc yet
  // e.g. race condition with RT changes that may be received later
  nodePosition = nodePosition || (previous && nodePositionByUuid[previous.element_uuid as string])

  if (nodePosition && Number.isInteger(nodePosition.pos)) return nodePosition
}

const groupPresencesByUuid = (
  nodePositionByUuid: { [uuid: string]: NodePosition },
  presences: UserElementPresences[],
) => {
  const groupedPresencesByUuid: { [uuid: string]: UserElementPresences[] } = {}

  presences.forEach((presence) => {
    const nodePosition = findNodePosition(nodePositionByUuid, presence)
    if (!nodePosition) return

    const {
      node: {
        attrs: { blockId },
      },
    } = nodePosition
    if (groupedPresencesByUuid[blockId]) {
      groupedPresencesByUuid[blockId].push(presence)
    } else {
      groupedPresencesByUuid[blockId] = [presence]
    }
  })

  return groupedPresencesByUuid
}

const buildDecorations = ({
  doc,
  presences,
}: {
  doc: Node
  presences: UserElementPresences[]
}): ElementPresenceState => {
  const roots: Root[] = []
  const widgets: Decoration[] = []

  const nodePositionByUuid = calcNodePositionByUuid(doc) // build a hash for quick lookups by uuid
  const groupedPresencesByUuid = groupPresencesByUuid(nodePositionByUuid, presences)

  Object.keys(groupedPresencesByUuid).forEach((uuid) => {
    const groupedPresences = groupedPresencesByUuid[uuid]
    const { node, pos, prevNode } = nodePositionByUuid[uuid]
    const users = groupedPresences.map((p) => p.current.user || p.current.anonymous_user)
    const container = document.createElement('div')
    const root = createRoot(container)
    roots.push(root)

    widgets.push(
      Decoration.widget(pos, () => {
        const elementClass = Styles[elementPresenceClass(node)]
        const prevElementClass = Styles[`after-${elementPresenceClass(prevNode)}`]
        container.className = classNames(Styles.elementPresence, elementClass, prevElementClass)
        root.render(<PresenceAvatars users={users} showMinCount={1} showMaxCount={2} />)
        return container
      }),
    )
  })

  return { roots, decorations: DecorationSet.create(doc, widgets) }
}

export const ElementPresencePlugin: Plugin = new Plugin({
  key: ElementPresencePluginKey,
  state: {
    init(config): ElementPresenceState {
      return {
        decorations: DecorationSet.create(config.doc, []),
        roots: [],
      }
    },
    apply(tr: Transaction, state: ElementPresenceState) {
      const presences = tr.getMeta(ElementPresencePlugin)
      if (presences) {
        state.roots.forEach((root) => root.unmount())
        return buildDecorations({ doc: tr.doc, presences })
      }

      return { ...state, decorations: state.decorations.map(tr.mapping, tr.doc) }
    },
  },
  props: {
    decorations(state) {
      return ElementPresencePlugin.getState(state).decorations
    },
  },
})
